/**
 * CR141: Forum Tools admin page
 * Canonical view for managing forum sources used by Scheduler + ingestion
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Plus, RefreshCw, Search, ExternalLink } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface ForumSource {
  id: string;
  name: string;
  source_type: "RFD" | "REDDIT" | "GENERIC_RSS" | "GENERIC_HTML";
  endpoint_url: string;
  is_enabled: boolean;
  keywords: string[] | null;
  ontario_only: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  RFD: "RedFlagDeals",
  REDDIT: "Reddit",
  GENERIC_RSS: "RSS Feed",
  GENERIC_HTML: "HTML Scraper",
};

export default function ForumTools() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<ForumSource | null>(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formSourceType, setFormSourceType] = useState<ForumSource["source_type"]>("RFD");
  const [formEndpointUrl, setFormEndpointUrl] = useState("");
  const [formIsEnabled, setFormIsEnabled] = useState(true);
  const [formKeywords, setFormKeywords] = useState("");
  const [formOntarioOnly, setFormOntarioOnly] = useState(true);
  const [formNotes, setFormNotes] = useState("");

  const { data: sources = [], isLoading, refetch } = useQuery({
    queryKey: ["forum-sources"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("community_sources")
        .select("*")
        .order("name");
      if (error) throw error;
      return data as ForumSource[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (source: {
      name: string;
      source_type: ForumSource["source_type"];
      endpoint_url: string;
      is_enabled: boolean;
      keywords: string[] | null;
      ontario_only: boolean;
      notes: string | null;
    }) => {
      if (editingSource) {
        const { error } = await supabase
          .from("community_sources")
          .update(source)
          .eq("id", editingSource.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("community_sources").insert([source]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forum-sources"] });
      toast({
        title: editingSource ? "Source updated" : "Source created",
        description: `Forum source "${formName}" has been saved.`,
      });
      setEditorOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase
        .from("community_sources")
        .update({ is_enabled: enabled })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forum-sources"] });
    },
  });

  const resetForm = () => {
    setFormName("");
    setFormSourceType("RFD");
    setFormEndpointUrl("");
    setFormIsEnabled(true);
    setFormKeywords("");
    setFormOntarioOnly(true);
    setFormNotes("");
    setEditingSource(null);
  };

  const openEditor = (source?: ForumSource) => {
    if (source) {
      setEditingSource(source);
      setFormName(source.name);
      setFormSourceType(source.source_type);
      setFormEndpointUrl(source.endpoint_url);
      setFormIsEnabled(source.is_enabled);
      setFormKeywords(source.keywords?.join(", ") || "");
      setFormOntarioOnly(source.ontario_only);
      setFormNotes(source.notes || "");
    } else {
      resetForm();
    }
    setEditorOpen(true);
  };

  const handleSave = () => {
    const keywords = formKeywords
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);

    saveMutation.mutate({
      name: formName.trim(),
      source_type: formSourceType,
      endpoint_url: formEndpointUrl.trim(),
      is_enabled: formIsEnabled,
      keywords: keywords.length > 0 ? keywords : null,
      ontario_only: formOntarioOnly,
      notes: formNotes.trim() || null,
    });
  };

  const filteredSources = sources.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.endpoint_url.toLowerCase().includes(search.toLowerCase())
  );

  const enabledCount = sources.filter((s) => s.is_enabled).length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Forum Tools</h1>
            <p className="text-muted-foreground">
              Manage forum sources for deal ingestion and scheduling
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={() => openEditor()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Source
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Sources</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{sources.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Enabled</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">{enabledCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Disabled</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-muted-foreground">
                {sources.length - enabledCount}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search forums..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Keywords</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredSources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No forum sources found
                  </TableCell>
                </TableRow>
              ) : (
                filteredSources.map((source) => (
                  <TableRow key={source.id}>
                    <TableCell className="font-medium">{source.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {SOURCE_TYPE_LABELS[source.source_type] || source.source_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <a
                        href={source.endpoint_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-primary hover:underline max-w-[200px] truncate"
                      >
                        {new URL(source.endpoint_url).hostname}
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </a>
                    </TableCell>
                    <TableCell>
                      {source.keywords?.length ? (
                        <div className="flex flex-wrap gap-1 max-w-[150px]">
                          {source.keywords.slice(0, 2).map((k) => (
                            <Badge key={k} variant="secondary" className="text-xs">
                              {k}
                            </Badge>
                          ))}
                          {source.keywords.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{source.keywords.length - 2}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={source.is_enabled}
                        onCheckedChange={(checked) =>
                          toggleMutation.mutate({ id: source.id, enabled: checked })
                        }
                      />
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDistanceToNow(new Date(source.updated_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => openEditor(source)}>
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Editor Dialog */}
        <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingSource ? "Edit Forum Source" : "Add Forum Source"}</DialogTitle>
              <DialogDescription>
                Configure a forum source for deal detection and ingestion.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g., RFD Hot Deals"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="type">Source Type *</Label>
                <Select value={formSourceType} onValueChange={(v) => setFormSourceType(v as ForumSource["source_type"])}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RFD">RedFlagDeals</SelectItem>
                    <SelectItem value="REDDIT">Reddit</SelectItem>
                    <SelectItem value="GENERIC_RSS">RSS Feed</SelectItem>
                    <SelectItem value="GENERIC_HTML">HTML Scraper</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="url">Endpoint URL *</Label>
                <Input
                  id="url"
                  type="url"
                  value={formEndpointUrl}
                  onChange={(e) => setFormEndpointUrl(e.target.value)}
                  placeholder="https://forums.redflagdeals.com/..."
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="keywords">Keywords (comma-separated)</Label>
                <Input
                  id="keywords"
                  value={formKeywords}
                  onChange={(e) => setFormKeywords(e.target.value)}
                  placeholder="gift card, bonus, discount"
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Optional notes about this source..."
                  rows={2}
                />
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="enabled"
                    checked={formIsEnabled}
                    onCheckedChange={setFormIsEnabled}
                  />
                  <Label htmlFor="enabled">Enabled</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="ontario"
                    checked={formOntarioOnly}
                    onCheckedChange={setFormOntarioOnly}
                  />
                  <Label htmlFor="ontario">Ontario Only</Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditorOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!formName.trim() || !formEndpointUrl.trim() || saveMutation.isPending}
              >
                {saveMutation.isPending ? "Saving..." : editingSource ? "Save Changes" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
